/**
 * Hotels Slices Module
 *
 * Re-exports all hotels slices and combines them
 */
import { combineReducers } from '@reduxjs/toolkit';

// Import reducers
import { detailsReducer, type DetailsState } from './detailsSlice';
import { popupsReducer, type HotelsPopupsState } from './popupsSlice';
import { bookingReviewReducer, type HotelBookingReviewState } from './bookingReviewSlice';
import { checkoutReducer, type HotelCheckoutState } from './checkoutSlice';
import { searchContextReducer, type SearchContextState } from './searchContextSlice';
import { hotelConfigReducer, type HotelConfigState } from './hotelConfigSlice';
import { filterCountReducer, type FilterCountState } from './filterCountSlice';
import { listingReducer, type HotelsListingState } from './listingSlice';

// Re-export details actions
export {
  setHotelDetailsFlags,
  clearHotelDetailsFlags,
  openGallery,
  closeGallery,
  openReviews,
  closeReviews,
  clearHotelDetails,
  resetDetailsState,
} from './detailsSlice';

// Re-export booking review actions
export {
  setHotelFareSummary,
  setOriginalFareSummary,
  clearPolicyRules,
  setTravellersFromTemplate,
  updateCouponFromChat,
  clearReviewError,
  clearPanFromChat,
  resetHotelBookingReviewState,
} from './bookingReviewSlice';

// Re-export checkout actions
export { resetHotelCheckoutState, clearCheckoutError } from './checkoutSlice';

// Re-export search context actions (for details page)
export {
  setDetailsSearchContext,
  setHotelSearchContext,
  setRoomStayCandidates,
  clearHotelSearchContext,
  resetSearchContextState,
} from './searchContextSlice';

// Re-export hotel config actions (for listing page)
export {
  setListingSearchContext,
  clearHotelConfig,
  resetHotelConfigState,
} from './hotelConfigSlice';

// Re-export filter count actions
export { clearFilterCount, resetFilterCountState } from './filterCountSlice';

// Re-export listing actions (for View-More functionality)
export {
  updateHotelsListing,
  setLoadingMore as setListingLoadingMore,
  clearHotelsListing,
  resetListingState,
} from './listingSlice';

// Re-export popups actions
export {
  showQnAGalleryPopup,
  showHotelsPopup,
  showHotelsErrorPopup,
  clearHotelsPopup,
  clearAllHotelsPopups,
  QNA_GALLERY_POPUP_KEY,
  API_ERROR_POPUP_KEY,
} from './popupsSlice';

// Re-export types
export type { DetailsState, HotelDetailsFlags } from './detailsSlice';
export type {
  HotelsPopupsState,
  QnAGalleryPopupData,
  PopupErrorDetails as HotelsPopupErrorDetails,
} from './popupsSlice';
export type { HotelBookingReviewState, ReviewError } from './bookingReviewSlice';
export type { HotelCheckoutState } from './checkoutSlice';
export type {
  SearchContextState,
  HotelSearchContext,
  RoomStayCandidates,
  DetailsSearchContextRaw,
} from './searchContextSlice';
export type { HotelConfigState, ListingSearchContext } from './hotelConfigSlice';
export type { FilterCountState } from './filterCountSlice';
export type { HotelsListingState, UpdateListingPayload } from './listingSlice';

// Combined state type
export interface HotelsState {
  readonly details: DetailsState;
  readonly popups: HotelsPopupsState;
  readonly bookingReview: HotelBookingReviewState;
  readonly checkout: HotelCheckoutState;
  readonly searchContext: SearchContextState;
  readonly config: HotelConfigState;
  readonly filterCount: FilterCountState;
  readonly listing: HotelsListingState;
}

// Combined reducer
export const hotelsReducer = combineReducers({
  details: detailsReducer,
  popups: popupsReducer,
  bookingReview: bookingReviewReducer,
  checkout: checkoutReducer,
  searchContext: searchContextReducer,
  config: hotelConfigReducer,
  filterCount: filterCountReducer,
  listing: listingReducer,
});
